import React, { useState, useEffect } from 'react';
import { BookOpen, ExternalLink, Lock, Sparkles } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription.js';

const CourseRecommendations = ({
  testTitle,
  totalScore,
  threshold = 80,
}) => {
  const { isPro, loading: subscriptionLoading } = useSubscription();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Only fetch courses if score is below threshold
  const shouldShowCourses = totalScore < threshold;

  useEffect(() => {
    if (shouldShowCourses && !subscriptionLoading) {
      fetchCourses();
    }
  }, [testTitle, shouldShowCourses, subscriptionLoading]);

  const fetchCourses = async () => {
    setLoading(true);
    setError(null);

    try {
      // Extract skill/domain from test title
      const skillQuery = testTitle
        .replace(/test|assessment|validation|skills/gi, '')
        .trim();

      // Curated list of top free courses for common topics
      const courseDatabase = {
        'frontend': [
          {
            title: 'Responsive Web Design',
            provider: 'freeCodeCamp',
            url: 'https://www.freecodecamp.org/learn/2022/responsive-web-design/',
            description: 'Learn HTML, CSS, and responsive design fundamentals',
          },
          {
            title: 'Frontend Web Development',
            provider: 'The Odin Project',
            url: 'https://www.theodinproject.com/paths/full-stack-javascript',
            description: 'Complete frontend development curriculum',
          },
          {
            title: 'HTML & CSS Tutorial',
            provider: 'W3Schools',
            url: 'https://www.w3schools.com/html/',
            description: 'Interactive HTML and CSS tutorials',
          },
        ],
        'backend': [
          {
            title: 'Backend Development and APIs',
            provider: 'freeCodeCamp',
            url: 'https://www.freecodecamp.org/learn/back-end-development-and-apis/',
            description: 'Node.js, Express, and MongoDB fundamentals',
          },
          {
            title: 'CS50 Web Programming',
            provider: 'Harvard (edX)',
            url: 'https://cs50.harvard.edu/web/',
            description: 'Web programming with Python and JavaScript',
          },
        ],
        'fullstack': [
          {
            title: 'Full Stack Open',
            provider: 'University of Helsinki',
            url: 'https://fullstackopen.com/en/',
            description: 'Modern full stack development with React, Node.js',
          },
          {
            title: 'The Odin Project - Full Stack JavaScript',
            provider: 'The Odin Project',
            url: 'https://www.theodinproject.com/paths/full-stack-javascript',
            description: 'Complete full stack JavaScript curriculum',
          },
        ],
        'react': [
          {
            title: 'React Official Tutorial',
            provider: 'React.dev',
            url: 'https://react.dev/learn',
            description: 'Official React documentation and tutorial',
          },
          {
            title: 'React - The Complete Guide',
            provider: 'freeCodeCamp (YouTube)',
            url: 'https://www.youtube.com/watch?v=bMknfKXIFA8',
            description: 'Comprehensive React course on YouTube',
          },
        ],
        'javascript': [
          {
            title: 'JavaScript Algorithms and Data Structures',
            provider: 'freeCodeCamp',
            url: 'https://www.freecodecamp.org/learn/javascript-algorithms-and-data-structures-v8/',
            description: 'Learn JavaScript fundamentals and algorithms',
          },
          {
            title: 'JavaScript.info',
            provider: 'javascript.info',
            url: 'https://javascript.info/',
            description: 'Modern JavaScript tutorial from basics to advanced',
          },
        ],
        'python': [
          {
            title: 'Scientific Computing with Python',
            provider: 'freeCodeCamp',
            url: 'https://www.freecodecamp.org/learn/scientific-computing-with-python/',
            description: 'Python fundamentals and scientific computing',
          },
          {
            title: 'Python for Everybody',
            provider: 'University of Michigan (Coursera)',
            url: 'https://www.coursera.org/specializations/python',
            description: 'Complete Python programming specialization',
          },
        ],
        'devops': [
          {
            title: 'DevOps Bootcamp',
            provider: 'freeCodeCamp (YouTube)',
            url: 'https://www.youtube.com/watch?v=j5Zsa_eOXeY',
            description: 'Complete DevOps course covering Docker, K8s, and more',
          },
        ],
        'java': [
          {
            title: 'Java Programming',
            provider: 'University of Helsinki',
            url: 'https://java-programming.mooc.fi/',
            description: 'Comprehensive Java programming course',
          },
        ],
      };

      // Match courses based on skill
      let matchedCourses = [];
      const lowerSkillQuery = skillQuery.toLowerCase();

      // Check for keyword matches
      for (const [key, value] of Object.entries(courseDatabase)) {
        if (lowerSkillQuery.includes(key)) {
          matchedCourses = value;
          break;
        }
      }

      // If no specific match, provide general resources
      if (matchedCourses.length === 0) {
        matchedCourses = [
          {
            title: 'freeCodeCamp',
            provider: 'freeCodeCamp',
            url: 'https://www.freecodecamp.org/',
            description: 'Free coding bootcamp with certifications',
          },
          {
            title: 'The Odin Project',
            provider: 'The Odin Project',
            url: 'https://www.theodinproject.com/',
            description: 'Free full-stack curriculum',
          },
          {
            title: 'CS50 Introduction to Computer Science',
            provider: 'Harvard (edX)',
            url: 'https://cs50.harvard.edu/x/',
            description: 'Harvard\'s introduction to computer science',
          },
        ];
      }

      setCourses(matchedCourses);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load course recommendations');
    } finally {
      setLoading(false);
    }
  };

  if (!shouldShowCourses) {
    return null;
  }

  if (subscriptionLoading || loading) {
    return (
      <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
          <h3 className="text-lg font-semibold text-gray-900">
            Loading Course Recommendations...
          </h3>
        </div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="text-center px-4">
            <Lock className="w-12 h-12 text-purple-600 mx-auto mb-3" />
            <h4 className="text-xl font-bold text-gray-900 mb-2">
              Upgrade to Get Relevant Courses
            </h4>
            <p className="text-gray-700 mb-4">
              Upgrade to Pro to access personalized course recommendations and improve your skills
            </p>
            <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
              Upgrade to Pro
            </button>
          </div>
        </div>

        {/* Blurred preview */}
        <div className="filter blur-sm select-none pointer-events-none">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Recommended Courses to Improve Your Skills
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Based on your test performance, we recommend these free courses:
          </p>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="p-4 bg-white rounded-lg border border-purple-100 hover:border-purple-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Course Title Here
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Course description and learning objectives...
                    </p>
                    <span className="inline-flex items-center text-xs text-purple-600 font-medium">
                      Provider Name
                    </span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-purple-600 ml-4 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          Recommended Courses to Improve Your Skills
        </h3>
        <span className="ml-auto px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
          PRO
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Based on your test performance (Score: {totalScore}%), we recommend these free courses to help you improve:
      </p>
      <div className="space-y-3">
        {courses.map((course, index) => (
          <a
            key={index}
            href={course.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block p-4 bg-white rounded-lg border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">
                  {course.title}
                </h4>
                <p className="text-sm text-gray-600 mb-2">{course.description}</p>
                <span className="inline-flex items-center text-xs text-purple-600 font-medium">
                  {course.provider}
                </span>
              </div>
              <ExternalLink className="w-4 h-4 text-purple-600 ml-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default CourseRecommendations;

